// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";

import { IAction, ActionType, raiseAction } from "actions";
import { IActionTypeSelectDashboardItem } from "actions";
import { DashboardPanel } from "types";
import { isSet } from "utils";
import { getPanelSelection, toDropdownItems } from "guiutils";
import { EnergySource, IEnergyMeter, ITariffCircuit } from "parameter";
import { IState } from "states/states";
import TariffCircuitListModel from "model/tariffcircuitlist";

import Dropdown from "view/comp/Dropdown";

import { ReactComponent as IconStepOut } from "static/iconStepOut.svg";
import cssContent from "content.scss";

interface IHierarchySelectExtProps {
  allowAllEnergySources: boolean;
  allowAllHierarchies: boolean;
  panel: DashboardPanel;
  dashboardIndex?: number;
  className?: string;
  emptyText?: JSX.Element;
}

interface IHierarchySelectProps {
  tariffCircuits: ITariffCircuit[];
  energyMeters: IEnergyMeter[];
  selectedDashboardIndex: number;
  selectedEnergySource: EnergySource | undefined;
  selectedHierarchyId: number | null | undefined;
}

const mapStateToProps = (state: IState, ownProps: IHierarchySelectExtProps) => {
  const { index, selection } = getPanelSelection(state, ownProps.panel, ownProps.dashboardIndex);
  const props: IHierarchySelectProps = {
    tariffCircuits: state.parameter?.tariffline ?? [],
    energyMeters: state.parameter?.energymeter ?? [],
    selectedEnergySource: selection?.energySource,
    selectedHierarchyId: selection?.hierarchyId,
    selectedDashboardIndex: index,
  };
  return props;
};

interface IHierarchySelectDispatch {
  onHierarchyChanged: (
    index: number,
    hierarchyId: number | null | undefined,
    hierarchyUp: boolean,
  ) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IHierarchySelectDispatch = {
    onHierarchyChanged: (
      index: number,
      hierarchyId: number | null | undefined,
      hierarchyUp: boolean,
    ) => {
      const payload: IActionTypeSelectDashboardItem = { index, hierarchyId, hierarchyUp };
      dispatch(raiseAction(ActionType.ACT_SELECT_HIERARCHY, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectCaption: {
    id: "Select hierarchy",
  },
  titleHierarchyUp: {
    id: "Hierarchy Up",
  },
});

class HierarchySelectComponent extends React.Component<
  IHierarchySelectProps &
    IHierarchySelectExtProps &
    IHierarchySelectDispatch &
    WrappedComponentProps
> {
  private getHierarchyName(id: number | null | undefined) {
    if (id === undefined && this.props.allowAllHierarchies) {
      return <FormattedMessage id="All hierarchies" />;
    }
    if (id === null || id === undefined) {
      return (
        <>
          <FormattedMessage id="Root" />
        </>
      );
    }
    return this.props.tariffCircuits.find((curItem, curIndex) => (curItem.id ?? curIndex) === id)
      ?.name;
  }

  public render() {
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    // in pie-chart view mode, we must not switch to all energy sources
    const hierarchyIds = tcModel.getHierarchyIds(
      this.props.selectedEnergySource,
      this.props.allowAllEnergySources,
      this.props.allowAllHierarchies,
    );
    const sourceItems = toDropdownItems(
      hierarchyIds,
      "selectedHierarchy",
      (name, value) =>
        this.props.onHierarchyChanged(this.props.selectedDashboardIndex, value, false),
      (value) => this.getHierarchyName(value) ?? "",
    );
    const hasMultipleHierarchies = this.props.allowAllHierarchies
      ? hierarchyIds.length > 2
      : hierarchyIds.length > 1;

    const levelUpEnabled = this.props.selectedHierarchyId !== undefined;
    return (
      <>
        {
          // there are always at least 2 items: undefined (all Hierarchies) and null (Wurzel)...
          // so everything > 2 is ok to show the control
          sourceItems.length > 2 ? (
            hasMultipleHierarchies ? (
              <>
                <Button
                  title={this.props.intl.formatMessage(messages.titleHierarchyUp)}
                  variant="secondary"
                  className="hierarchyUp"
                  disabled={!levelUpEnabled}
                  onClick={() => {
                    const tc = this.props.tariffCircuits.find(
                      (curItem, curIndex) =>
                        (curItem.id ?? curIndex) === this.props.selectedHierarchyId,
                    );
                    this.props.selectedHierarchyId !== undefined &&
                      this.props.onHierarchyChanged(
                        this.props.selectedDashboardIndex,
                        !isSet(this.props.selectedHierarchyId)
                          ? undefined
                          : isSet(tc?.parent)
                          ? tc.parent
                          : null,
                        true,
                      );
                  }}
                >
                  <IconStepOut
                    fill={levelUpEnabled === false ? cssContent.BUTTON_DISABLED : undefined}
                  />
                </Button>
                <div id="hierarchyselect" className={this.props.className}>
                  <Dropdown
                    items={sourceItems}
                    caption={this.props.intl.formatMessage(messages.selectCaption)}
                    curItem={this.getHierarchyName(this.props.selectedHierarchyId)}
                  />
                </div>
              </>
            ) : (
              this.props.emptyText ?? <></>
            )
          ) : (
            <></>
          )
        }
      </>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(HierarchySelectComponent));
